import React from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button, InputAdornment, TextField, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import { maskedLabel, recentlyChanged, useUnlockedAccounts, usePayoutKey } from "../../components/PayoutKey";
import { useAuth } from "../../common/auth";
import { MarkPaidPayload, WithdrawalRequest, getWithdrawalRequests, markWithdrawalPaid } from "../../common/requests/transactions/endpoints";
import { formatDate } from "../../common/constant/dates";
import { C } from "../../common/theme";
import { formatAge, formatCount, formatDuration, formatMoney } from "../../common/utils/format";
import {
  Banner,
  Column,
  CopyValue,
  DataTable,
  KpiCard,
  KpiStrip,
  PageShell,
  StatusPill,
  errorMessage,
  useResource,
  useToast,
  useUrlParam,
} from "../../ui";
import { downloadCsv, toDateInput } from "../Sales/salesData";
import MarkPaidDialog from "./components/MarkPaidDialog";
import PayoutDialog from "./components/PayoutDialog";
import { filterPayouts, findPayout, isPaid, parseView, payoutsToCsv, statusLabel, totalsByCurrency } from "./components/payoutUtils";

const DAY = 86400000;

/** Seller withdrawal requests: who's waiting to be paid, everything needed to
 * pay them, and a record of how each payout was made. */
export default function Withdrawals() {
  const { idToken, adminUser } = useAuth();
  const toast = useToast();
  const canPay = adminUser?.role === "superadmin";
  const [searchParams, setSearchParams] = useSearchParams();
  const view = parseView(searchParams.get("view"));
  const [payoutParam, setPayoutParam] = useUrlParam("payout");
  const [query, setQuery] = React.useState("");
  const [payTarget, setPayTarget] = React.useState<WithdrawalRequest | null>(null);
  const [paying, setPaying] = React.useState(false);
  const [payError, setPayError] = React.useState<string | null>(null);

  const res = useResource(async () => (await getWithdrawalRequests(idToken!)).data, [idToken], {
    enabled: !!idToken,
    errorText: "Couldn't load payouts.",
  });
  const data = res.data;
  const requests = React.useMemo(() => data?.requests ?? [], [data]);
  const pending = requests.filter((r) => !isPaid(r));
  const paidCount = requests.length - pending.length;
  const paidLast30 = requests.filter((r) => isPaid(r) && r.payout && Date.now() - new Date(r.payout.paid_at).getTime() < 30 * DAY);
  const oldestPendingMs = pending.length ? Math.max(...pending.map((r) => Date.now() - new Date(r.request_date).getTime())) : null;
  const oldestPendingDays = oldestPendingMs === null ? null : Math.floor(oldestPendingMs / DAY);

  const visible = React.useMemo(() => filterPayouts(requests, view, query), [requests, view, query]);
  const openRequest = findPayout(requests, payoutParam);

  const setView = (next: string) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === "pending") params.delete("view");
        else params.set("view", next);
        return params;
      },
      { replace: true }
    );

  const startPay = (r: WithdrawalRequest) => {
    setPayError(null);
    setPayTarget(r);
  };

  const handlePay = async (request: WithdrawalRequest, payload: MarkPaidPayload) => {
    if (!idToken) return;
    setPaying(true);
    setPayError(null);
    try {
      const { data: result } = await markWithdrawalPaid(request.withdrawal_id, payload, idToken);
      setPayTarget(null);
      toast(
        result.already_paid
          ? "This request was already marked as paid."
          : result.payout_recorded
            ? `Payout to ${request.username ?? request.email} recorded`
            : "Marked as paid. The reference is in the activity log (payout records aren't installed yet).",
        "success"
      );
      res.reload();
    } catch (err) {
      setPayError(errorMessage(err, "Couldn't record this payout."));
    } finally {
      setPaying(false);
    }
  };

  const columns: Column<WithdrawalRequest>[] = [
    {
      key: "seller",
      header: "Seller",
      card: "title",
      minWidth: 180,
      sortValue: (r) => (r.username ?? r.email ?? "").toLowerCase(),
      render: (r) => {
        const seller = data?.sellers[r.user_id];
        const sub = [seller?.country, seller?.whatsapp_number].filter(Boolean).join(" · ") || r.email || r.user_id;
        return (
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>{r.username ?? r.email ?? "–"}</Box>
            <Box sx={{ fontSize: 12.5, fontWeight: 400, color: C.muted, overflowWrap: "anywhere" }}>{sub}</Box>
          </Box>
        );
      },
    },
    {
      key: "requested",
      header: "Requested",
      card: "subtitle",
      sortValue: (r) => new Date(r.request_date).getTime(),
      render: (r) => (
        <Box sx={{ whiteSpace: "nowrap" }}>
          <Box component="span" sx={{ display: { xs: "inline", md: "block" } }}>
            {formatDate(r.request_date)}
          </Box>
          <Box component="span" sx={{ fontSize: 12.5, color: C.muted, display: { xs: "inline", md: "block" }, ml: { xs: 0.75, md: 0 } }}>
            {formatAge(r.request_date)}
          </Box>
        </Box>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      card: "aside",
      align: "right",
      sortValue: (r) => r.amount,
      render: (r) => (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
          {r.currency_mixed && (
            <Tooltip title="This seller's withdrawn earnings are in more than one currency, so this total mixes them. Open the payout for the breakdown.">
              <WarningAmberIcon sx={{ fontSize: 16, color: C.coralText }} />
            </Tooltip>
          )}
          <Box component="span" sx={{ typography: "h6" }}>
            {formatMoney(r.amount, r.currency)}
          </Box>
        </Box>
      ),
    },
    {
      key: "payto",
      header: "Pay to / contact",
      // Copy buttons can't sit inside a phone card (it is one big button); the dialog has them.
      card: "hidden",
      hideBelow: 1100,
      render: (r) => {
        if (isPaid(r) && r.payout?.payout_reference) {
          return (
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ fontSize: 12, color: C.muted }}>Reference</Box>
              <CopyValue value={r.payout.payout_reference} mono />
            </Box>
          );
        }
        const account = data?.sellers[r.user_id]?.payout_account;
        if (account) {
          const plain = unlocked[r.user_id];
          return (
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ fontSize: 12, color: C.muted, display: "flex", alignItems: "center", gap: 0.5 }}>
                {account.method === "paynow" ? "PayNow" : account.bank_name ?? "Bank"}
                {account.bank_code ? ` · ${account.bank_code}` : ""}
                {recentlyChanged(account) && (
                  <Tooltip title="Changed in the last 7 days. Open it to check before paying.">
                    <WarningAmberIcon sx={{ fontSize: 15, color: C.coralText }} />
                  </Tooltip>
                )}
              </Box>
              {plain ? (
                <CopyValue value={plain} mono />
              ) : (
                <Box component="span" className="mono" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: C.muted, fontSize: 13 }}>
                  <LockOutlinedIcon sx={{ fontSize: 14 }} />
                  {maskedLabel(account).split(" ").slice(-2).join(" ")}
                </Box>
              )}
            </Box>
          );
        }
        const phone = data?.sellers[r.user_id]?.whatsapp_number;
        return phone ? (
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 12, color: C.muted }}>WhatsApp</Box>
            <CopyValue value={phone} mono />
          </Box>
        ) : (
          <Box component="span" sx={{ color: C.muted, fontSize: 13 }}>
            No payout details or WhatsApp
          </Box>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      card: "aside",
      sortValue: (r) => (isPaid(r) ? 1 : 0),
      render: (r) => <StatusPill tone={isPaid(r) ? "success" : "warning"}>{statusLabel(r)}</StatusPill>,
    },
  ];
  if (canPay && view !== "paid") {
    columns.push({
      key: "action",
      header: "",
      card: "hidden",
      align: "right",
      render: (r) =>
        isPaid(r) ? null : (
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              startPay(r);
            }}
            sx={{ whiteSpace: "nowrap", minHeight: 36 }}
          >
            Mark paid
          </Button>
        ),
    });
  }

  // Bank details: locked until the owner unlocks this tab with the payout key.
  const { key, requestUnlock, lock } = usePayoutKey();
  const lockedAccounts = React.useMemo(
    () => Object.fromEntries(Object.entries(data?.sellers ?? {}).map(([id, s]) => [id, s.payout_account ?? null])),
    [data]
  );
  const unlocked = useUnlockedAccounts(lockedAccounts, "payouts");
  const firstCiphertext = Object.values(lockedAccounts).find(Boolean)?.ciphertext;
  const keyBanner = !firstCiphertext ? null : key ? (
    <Banner tone="success" icon={<LockOpenOutlinedIcon />} title="Bank details unlocked in this tab." action={<Button variant="outlined" size="small" onClick={lock}>Lock now</Button>}>
      They lock again after 15 minutes idle or when you close the tab. Each unlock is in the activity log.
    </Banner>
  ) : (
    <Banner tone="neutral" icon={<LockOutlinedIcon />} title="Bank details are locked." action={<Button variant="contained" size="small" onClick={() => requestUnlock(firstCiphertext)}>Unlock</Button>}>
      Unlock with your payout key to see where to send each payout.
    </Banner>
  );

  const banner = !data ? null : !data.payouts_recorded ? (
    <Banner tone="warning" title="Payout records aren't installed.">
      Payout dates and references aren't stored here yet (they still go in the activity log). Run{" "}
      <code>admin-backend/sql/migrations/002_withdrawal_payouts.sql</code> on the database.
    </Banner>
  ) : pending.length ? (
    <Banner tone={oldestPendingDays !== null && oldestPendingDays >= 3 ? "warning" : "neutral"} title={`${formatCount(pending.length)} to pay, ${totalsByCurrency(pending)}.`}>
      Oldest waiting {formatDuration(oldestPendingMs ?? 0)}. Pay by bank transfer, then mark it paid with the transfer reference.
      {!canPay && " Only superadmins can mark payouts as paid."}
    </Banner>
  ) : (
    <Banner tone="success" title="All paid.">
      No seller is waiting for a payout.
    </Banner>
  );

  return (
    <PageShell
      title={
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
          Payouts
          {data && pending.length > 0 && <StatusPill tone="warning">{formatCount(pending.length)} to pay</StatusPill>}
        </Box>
      }
      subtitle="Sellers withdraw their completed earnings. Pay by bank transfer, then record the reference here."
      actions={
        <>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => res.reload()} disabled={res.status === "loading"} sx={{ minHeight: 44 }}>
            Reload
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            disabled={!visible.length}
            onClick={() => downloadCsv(`inflos-payouts_${view}_${toDateInput(new Date())}.csv`, payoutsToCsv(visible))}
            sx={{ minHeight: 44 }}
          >
            CSV
          </Button>
        </>
      }
      tabs={{
        value: view,
        onChange: setView,
        items: [
          { value: "pending", label: "Pending", count: data ? pending.length : null },
          { value: "paid", label: "Paid", count: data ? paidCount : null },
          { value: "all", label: "All", count: data ? requests.length : null },
        ],
      }}
    >
      {banner}
      {keyBanner}
      {res.error && data && (
        <Banner tone="danger" title="Reload failed." action={<Button onClick={() => res.reload()}>Try again</Button>}>
          {res.error}
        </Banner>
      )}

      <KpiStrip>
        <KpiCard
          title="To pay"
          value={data ? formatCount(pending.length) : "–"}
          tone={pending.length ? "warning" : "default"}
          sub={!data ? undefined : pending.length ? "requests waiting" : "all paid"}
          onClick={() => setView("pending")}
          active={view === "pending"}
        />
        <KpiCard title="Amount to pay" value={data ? totalsByCurrency(pending) : "–"} help="Summed per currency" />
        <KpiCard
          title="Oldest request"
          value={oldestPendingMs === null ? "–" : formatDuration(oldestPendingMs)}
          tone={oldestPendingDays !== null && oldestPendingDays >= 3 ? "warning" : "default"}
          sub={!data ? undefined : oldestPendingDays === null ? "nothing waiting" : "since requested"}
        />
        <KpiCard
          title="Paid in 30 days"
          value={data ? totalsByCurrency(paidLast30) : "–"}
          sub={data ? `${formatCount(paidLast30.length)} payout${paidLast30.length === 1 ? "" : "s"}` : undefined}
          help="Only payouts recorded with a date (after payout records were installed)"
          onClick={() => setView("paid")}
          active={view === "paid"}
        />
      </KpiStrip>

      <TextField
        placeholder="Search seller, email or reference"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        fullWidth
        sx={{ maxWidth: { sm: 420 } }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
          htmlInput: { "aria-label": "Search payouts" },
        }}
      />

      <DataTable
        aria-label="Payouts"
        rows={visible}
        columns={columns}
        getRowId={(r) => r.withdrawal_id}
        loading={res.status === "loading" || res.status === "idle"}
        error={res.error}
        onRetry={res.reload}
        empty={view === "pending" && !query ? "Nothing to pay. Every request has been paid." : "No requests match."}
        onRowClick={(r) => setPayoutParam(r.withdrawal_id.trim(), true)}
        selectedId={openRequest?.withdrawal_id ?? null}
      />

      <PayoutDialog
        open={!!(payoutParam ?? "").trim()}
        loading={!data && res.status !== "error"}
        request={openRequest}
        seller={openRequest ? data?.sellers[openRequest.user_id] : undefined}
        plainAccount={openRequest ? unlocked[openRequest.user_id] : undefined}
        payoutsRecorded={!!data?.payouts_recorded}
        canPay={canPay}
        onPay={startPay}
        onClose={() => setPayoutParam(null)}
      />
      <MarkPaidDialog
        request={payTarget}
        seller={payTarget ? data?.sellers[payTarget.user_id] : undefined}
        busy={paying}
        error={payError}
        onCancel={() => setPayTarget(null)}
        onConfirm={handlePay}
      />
    </PageShell>
  );
}

